import { useState, useEffect } from 'react';
import { GetToken } from '../features/authentication/hooks/useToken.jsx';
import BaseUrl from './baseUrl';
import { fallbackAuthApiKey, requestTimeoutMs } from '../config/runtimeConfig';


function useFetch(url, params, notFoundResult = null) {
  const [variables, setVariables] = useState(params);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    get_private(params);
  }, [url])

  function refetch(prms) {
    const combined = { ...variables, ...prms };
    const filteredCombined = Object.fromEntries(
      Object.entries(combined).filter(([, value]) => value !== null)
    );

    get_private(filteredCombined);
  }

  function get_private(prms) {
    if (url) {
      const objString = prms ? '?' + Object.entries(prms).map(([key, value]) => {
        if (Array.isArray(value)) {
          // If the value is an array, repeat the parameter for each element
          return value.map((element) => `${key}=${encodeURIComponent(element)}`).join('&');
        } else {
          // If the value is not an array, just include the parameter once
          return `${key}=${encodeURIComponent(value)}`;
        }
      }).join('&') : '';

      setLoading(true);
      Get(url + objString, (r) => {
        setLoading(false);
        setVariables(prms);
        setError(null);
        setData(r);
      }, (error, code) => {
        setLoading(false);
        if (code == 404 && notFoundResult != null) {
          setVariables(prms);
          setError(null);
          setData(notFoundResult);
        } else {
          let errorMsg = code === 0 ? 'Connection error — please try again' : String(error || `HTTP ${code}`);
          if (code !== 0) {
            try {
              const parsed = JSON.parse(error);
              errorMsg = parsed?.error || parsed?.message || errorMsg;
            } catch {}
          }
          setError(errorMsg);
        }
      });
    } else {
      setLoading(false);
      setData();
    }
  }

  return { data, loading, error, variables, refetch: refetch }
}

function Get(url, onSuccess, onError, options) {
  let xhr = new XMLHttpRequest();
  let fullUrl = BaseUrl + url;
  if (url.startsWith("https")) fullUrl = url;
  let settled = false;
  const timeoutMs = options?.timeoutMs ?? requestTimeoutMs;

  const finish = (handler, ...args) => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    handler(...args);
  };

  const timer = setTimeout(() => {
    xhr.abort();
    finish(onError, 'Request timed out', 408);
  }, timeoutMs);

  // open a connection
  xhr.open("GET", fullUrl, true);

  // Set the request header i.e. which type of content you are sending
  xhr.setRequestHeader("Content-Type", "application/json");
  const authHeader = options?.authorization;
  if (authHeader) {
    xhr.setRequestHeader("Authorization", authHeader);
  } else {
    const token = GetToken()?.token;
    if (token) {
      const raw = String(token);
      const authHeader = /^Bearer\s/i.test(raw) ? raw : `Bearer ${raw}`;
      xhr.setRequestHeader("Authorization", authHeader);
    } else if (fallbackAuthApiKey) {
      // Some self-hosted endpoints accept API key in Authorization header.
      xhr.setRequestHeader("Authorization", fallbackAuthApiKey);
    }
  }
  if (options?.withCredentials) {
    xhr.withCredentials = true;
  }
  const runtimeApiKey = options?.apiKey || fallbackAuthApiKey;
  if (runtimeApiKey) {
    xhr.setRequestHeader("X-API-Key", runtimeApiKey);
  }

  // Create a state change callback
  xhr.onreadystatechange = function () {
    if (xhr.readyState === 4) {
      if (xhr.status >= 200 && xhr.status <= 205) {
        try {
          finish(onSuccess, JSON.parse(xhr.response));
        }
        catch (e) {
          finish(onError, e);
        }
      }
      else if (xhr.status === 0 && xhr.statusText === 'abort') {
        // timeout handler already reported the error
      }
      else {
        finish(onError, xhr.response, xhr.status);
      }
    }
  };

  // Sending data with the request
  xhr.send();
}

export { useFetch, Get };